// OFFICIAL STORE SERVICE (B2C) - Uses 'orders' table
// This service handles B2C transactions for the Official Store only.
// DO NOT use for User Marketplace (C2C) - that uses 'marketplace_transactions' table.

import { supabase } from '../lib/supabase';
import inventoryService from './inventoryService';

/**
 * Creates a new order with orchestrated inventory -> database flow
 * @param {Object} orderData - Order information
 * @param {Array} cartItems - Array of cart items with product details
 * @returns {Promise<Object>} Created order with ID
 */
export const createOrder = async (orderData, cartItems) => {
  // 🚀 CRITICAL DEBUG: Log incoming cart items
  console.log('🚀 createOrder STARTED. Cart Items:', cartItems);
  
  // ❌ VALIDATION: Check if cart is empty
  if (!cartItems || cartItems?.length === 0) {
    console.error('❌ CRITICAL ERROR: Attempted to create order with EMPTY cart!');
    throw new Error('Sepetiniz boş görünüyor. Lütfen sayfayı yenileyip tekrar deneyin.');
  }

  let stockDeducted = false;
  let orderItemsForReversal = [];

  try {
    // Get current user - CRITICAL: Must be authenticated
    const { data: { user }, error: userError } = await supabase?.auth?.getUser();
    
    if (userError || !user) {
      throw new Error('User not authenticated. Please log in to place an order.');
    }

    // STEP 1: Update Inventory (Atomic Stock Deduction)
    console.log('Step 1: Updating inventory...');
    orderItemsForReversal = cartItems?.map(item => ({
      product_id: item?.id,
      quantity: item?.quantity,
      price_at_purchase: item?.price
    }));

    try {
      await inventoryService?.updateStockAfterOrder(orderItemsForReversal);
      stockDeducted = true;
      console.log('Inventory updated successfully');
    } catch (inventoryError) {
      console.error('Inventory update failed:', inventoryError);
      throw new Error(`Yetersiz Stok: ${inventoryError.message}`);
    }

    // STEP 2: Create Order Record in Database
    console.log('Step 2: Creating order record...');
    
    // Prepare payload with strict data types
    const orderPayload = {
      user_id: user?.id,
      total_amount: Number(orderData?.totalAmount),
      status: 'pending',
      payment_status: 'pending',
      shipping_address: orderData?.shippingAddress
    };
    
    console.log('Sending Order Payload:', orderPayload);
    
    const { data: order, error: orderError } = await supabase?.from('orders')?.insert([orderPayload])?.select()?.single();

    if (orderError) {
      console.error('Error creating order:', orderError);
      console.error('CRITICAL: Stock deducted but order creation failed. Attempting stock reversal...');
      await attemptStockReversal(orderItemsForReversal);
      throw new Error(`Sipariş oluşturulamadı: ${orderError.message}`);
    }

    // 📝 DEBUG: Map and validate order items before insertion
    const orderItems = cartItems?.map(item => ({
      order_id: order?.id,
      product_id: item?.id || item?.productId, // Fallback check
      quantity: item?.quantity,
      price_at_purchase: Number(item?.price),
      // Variant fields — null for non-variant products (backward compat)
      selected_color: item?.selectedColor || null,
      selected_size: item?.selectedSize || null,
      variant_id: item?.variantId || null,
    }));
    
    console.log('📝 Mapped Order Items to Insert:', orderItems);
    
    // ❌ VALIDATION: Check for missing product_id
    if (orderItems?.some(i => !i?.product_id)) {
      console.error('❌ INVALID ITEM DETECTED: Product ID is missing in one of the items!', orderItems);
      throw new Error('Ürün kimliği doğrulanamadı.');
    }

    // 🔥 EXPLICIT INSERT with detailed error handling
    const { data: insertedItems, error: itemsError } = await supabase?.from('order_items')?.insert(orderItems)?.select();

    if (itemsError) {
      console.error('❌ SUPABASE INSERT ERROR (order_items):', itemsError);
      console.error('CRITICAL: Order created but items insertion failed. Rolling back...');
      await supabase?.from('orders')?.delete()?.eq('id', order?.id);
      await attemptStockReversal(orderItemsForReversal);
      throw new Error(`Sipariş kalemleri kaydedilemedi: ${itemsError.message}`);
    } else {
      console.log('✅ SUCCESS: Inserted items count:', insertedItems?.length);
    }

    console.log('Order created successfully:', order?.id);
    return {
      ...order,
      transactionId: null,
      paymentStatus: null
    };
  } catch (error) {
    console.error('Error in createOrder:', error);
    throw error;
  }
};

/**
 * Attempts to reverse stock deduction when order creation fails
 * @param {Array} orderItems - Items to reverse stock for
 */
const attemptStockReversal = async (orderItems) => {
  try {
    console.log('Attempting stock reversal for', orderItems?.length, 'items...');
    
    for (const item of orderItems) {
      // Call increment stock RPC or manual update
      const { error } = await supabase
        ?.from('products')
        ?.update({ 
          stock: supabase?.raw(`stock + ${item?.quantity}`)
        })
        ?.eq('id', item?.product_id);

      if (error) {
        console.error(`Failed to reverse stock for product ${item?.product_id}:`, error);
        // Log to monitoring system in production
        console.error('CRITICAL: Manual stock adjustment required for product:', item?.product_id, 'Quantity:', item?.quantity);
      } else {
        console.log(`Stock reversed for product ${item?.product_id}: +${item?.quantity}`);
      }
    }
  } catch (error) {
    console.error('Stock reversal failed:', error);
    // In production, this should trigger alerts to operations team
    console.error('CRITICAL: Stock reversal failed. Manual intervention required.');
    console.error('Items requiring manual adjustment:', JSON.stringify(orderItems));
  }
};

/**
 * Formats a raw Supabase order row for admin display.
 * Safely parses shipping_address JSON and extracts name/address.
 */
const formatOrderForAdmin = (order, profilesMap = {}) => {
  // 1. Safely Parse Address (JSONB may already be an object)
  let addressObj = {};
  if (order?.shipping_address) {
    try {
      addressObj = typeof order?.shipping_address === 'string'
        ? JSON.parse(order?.shipping_address)
        : order?.shipping_address;
    } catch (e) {
      console.warn('Failed to parse shipping_address:', e);
    }
  }

  // DEBUG: Log the raw shipping_address to understand actual structure
  console.log('🔍 RAW shipping_address for order', order?.id, ':', JSON.stringify(order?.shipping_address));
  console.log('🔍 Parsed addressObj:', addressObj);

  // 2. Get profile from the separately-fetched profiles map
  const profile = profilesMap?.[order?.user_id] || {};

  // 3. Extract Name — CheckoutPage saves ContactName field
  // Priority: order.full_name (direct DB column) > ContactName (from checkout form) > profile full_name > other fields
  const rawName =
    (order?.full_name && order?.full_name?.trim() !== '' ? order?.full_name : null) ||
    addressObj?.ContactName ||
    addressObj?.AdSoyad ||
    addressObj?.ad_soyad ||
    addressObj?.full_name ||
    addressObj?.name ||
    addressObj?.firstName ||
    profile?.full_name ||
    profile?.first_name ||
    null;

  // Failsafe: never display raw UUIDs (UUIDs are exactly 36 chars with 4 dashes)
  const isUUID = rawName && typeof rawName === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i?.test(rawName);
  const adminDisplayName = (rawName && !isUUID) ? rawName : (order?.user_id ? 'Kayıtlı Müşteri' : 'Misafir');

  // 4. Extract Address — CheckoutPage saves Address, City, ZipCode fields
  const addrText =
    addressObj?.Address ||
    addressObj?.Adres ||
    addressObj?.adres ||
    addressObj?.address ||
    addressObj?.address_line ||
    addressObj?.addressLine ||
    addressObj?.street ||
    '';
  const district =
    addressObj?.İlçe ||
    addressObj?.ilce ||
    addressObj?.District ||
    addressObj?.district ||
    addressObj?.ilçe ||
    '';
  const city =
    addressObj?.City ||
    addressObj?.Şehir ||
    addressObj?.sehir ||
    addressObj?.city ||
    addressObj?.şehir ||
    '';
  const adminDisplayAddress = [addrText, district, city]?.filter(Boolean)?.join(', ') || '-';

  console.log('✅ Resolved name:', adminDisplayName, '| address:', adminDisplayAddress);

  return { ...order, adminDisplayName, adminDisplayAddress };
};

/**
 * Fetches all orders for admin dashboard.
 * Uses a two-step approach to avoid FK ambiguity on orders.user_id
 * (which references both auth.users AND user_profiles).
 * Step 1: Fetch orders without any user join (avoids FK ambiguity)
 * Step 2: Fetch user_profiles separately and merge by user_id
 * @returns {Promise<Array>} Array of all orders with user and product details
 */
export const getAllOrdersForAdmin = async () => {
  try {
    // STEP 1: Fetch orders without any user join (avoids FK ambiguity)
    const { data: orders, error: ordersError } = await supabase
      ?.from('orders')
      ?.select(`
        *,
        order_items (
          id,
          quantity,
          price_at_purchase,
          products ( id, title, images, image_urls )
        )
      `)
      ?.order('created_at', { ascending: false });

    if (ordersError) {
      console.error('Error fetching orders:', ordersError);
      throw ordersError;
    }

    console.log('📦 Raw orders count:', orders?.length);
    if (orders?.length > 0) {
      console.log('📦 First raw order sample:', JSON.stringify(orders?.[0], null, 2));
    }

    // STEP 2: Collect unique user_ids and fetch profiles separately
    const userIds = [...new Set((orders || []).map(o => o?.user_id).filter(Boolean))];
    let profilesMap = {};

    if (userIds?.length > 0) {
      const { data: profiles, error: profilesError } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, first_name, last_name, email')
        ?.in('id', userIds);

      if (profilesError) {
        console.warn('Could not fetch user profiles:', profilesError?.message);
      } else {
        // Build a map: user_id -> profile
        profilesMap = (profiles || [])?.reduce((acc, p) => {
          acc[p.id] = p;
          return acc;
        }, {});
        console.log('👤 Profiles fetched:', Object.keys(profilesMap)?.length);
      }
    }

    // STEP 3: Merge and format
    return (orders || [])?.map(order => formatOrderForAdmin(order, profilesMap));
  } catch (error) {
    console.error('Error in getAllOrdersForAdmin:', error);
    throw error;
  }
};

/**
 * Fetches order details by ID
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} Order details with items
 */
export const getOrderById = async (orderId) => {
  try {
    const { data: order, error: orderError } = await supabase?.from('orders')?.select(`
        *,
        order_items (
          *,
          products (
            id,
            title,
            images,
            price
          )
        )
      `)?.eq('id', orderId)?.single();

    if (orderError) {
      console.error('Error fetching order:', orderError);
      throw orderError;
    }

    return order;
  } catch (error) {
    console.error('Error in getOrderById:', error);
    throw error;
  }
};

/**
 * Fetches all orders for the current user with joined data
 * @returns {Promise<Array>} Array of user orders with items and products
 */
export const getUserOrders = async () => {
  try {
    const { data: { user }, error: userError } = await supabase?.auth?.getUser();
    
    if (userError) {
      throw new Error('User not authenticated');
    }

    // Auto-link any orphan guest orders placed with this email before fetching
    if (user?.id && user?.email) {
      try {
        await supabase?.rpc('link_orphan_guest_orders', {
          current_user_id: user?.id,
          current_user_email: user?.email
        });
      } catch (err) {
        console.error('Order linking failed silently', err);
      }
    }

    const { data: orders, error: ordersError } = await supabase
      ?.from('orders')
      ?.select(`
        *,
        order_items (
          *,
          products ( * )
        )
      `)
      ?.eq('user_id', user?.id)
      ?.order('created_at', { ascending: false });

    console.log('RAW SUPABASE RESPONSE (User):', orders);

    if (ordersError) {
      console.error('Error fetching user orders:', ordersError);
      throw ordersError;
    }

    return orders || [];
  } catch (error) {
    console.error('Error in getUserOrders:', error);
    throw error;
  }
};

// Export service object with all methods
export default {
  createOrder,
  getAllOrdersForAdmin,
  getUserOrders
};